/**
 * Platform User Service
 */

import apiClient from './apiClient';
import { API_ENDPOINTS } from '../config/api';
import type { PlatformUser, PlatformRole } from '../types';

export interface CreatePlatformUserDto {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  roleId?: string;
}

export interface UpdatePlatformUserDto {
  firstName?: string;
  lastName?: string;
  password?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'PENDING';
  roleId?: string;
}

export const platformUserService = {
  /**
   * Get all platform users
   */
  async getAll(): Promise<PlatformUser[]> {
    const response = await apiClient.get<PlatformUser[]>(API_ENDPOINTS.PLATFORM_USERS);
    return response.data;
  },

  /**
   * Get all platform roles
   */
  async getRoles(): Promise<PlatformRole[]> {
    const response = await apiClient.get<PlatformRole[]>(API_ENDPOINTS.PLATFORM_ROLES);
    return response.data;
  },

  /**
   * Get a single platform user
   */
  async getById(id: string): Promise<PlatformUser> {
    const response = await apiClient.get<PlatformUser>(
      `${API_ENDPOINTS.PLATFORM_USERS}/${id}`
    );
    return response.data;
  },

  /**
   * Create a new platform user
   */
  async create(dto: CreatePlatformUserDto): Promise<PlatformUser> {
    const response = await apiClient.post<PlatformUser>(
      API_ENDPOINTS.PLATFORM_USERS,
      dto
    );
    return response.data;
  },

  /**
   * Update a platform user
   */
  async update(id: string, dto: UpdatePlatformUserDto): Promise<PlatformUser> {
    const response = await apiClient.patch<PlatformUser>(
      `${API_ENDPOINTS.PLATFORM_USERS}/${id}`,
      dto
    );
    return response.data;
  },

  /**
   * Delete a platform user
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`${API_ENDPOINTS.PLATFORM_USERS}/${id}`);
  },
};
